import { useState, useEffect, useRef } from 'react';
import {
  Search,
  Loader2,
  ChevronDown,
  CheckCircle2,
  Mail,
  Eye,
  MousePointerClick,
  AlertTriangle,
  Clock,
  XCircle,
  ShieldAlert,
  Send,
  ArrowUpRight,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { parseDisplayName, formatDate } from '@/lib/utils';
import { useEmailHistory, useEmailStats } from '@/api';
import type { EmailHistory } from '@/types/outreach';
import styles from './MailingHistory.module.css';

const STATUS_CONFIG: Record<string, { icon: typeof Mail; className: string; label: string }> = {
  QUEUED: { icon: Clock, className: 'statusQueued', label: 'Queued' },
  DELIVERED: { icon: CheckCircle2, className: 'statusDelivered', label: 'Delivered' },
  OPENED: { icon: Eye, className: 'statusOpened', label: 'Opened' },
  CLICKED: { icon: MousePointerClick, className: 'statusClicked', label: 'Clicked' },
  BOUNCED: { icon: XCircle, className: 'statusBounced', label: 'Bounced' },
  DROPPED: { icon: XCircle, className: 'statusDropped', label: 'Dropped' },
  DEFERRED: { icon: Clock, className: 'statusDeferred', label: 'Deferred' },
  SPAM: { icon: ShieldAlert, className: 'statusSpam', label: 'Spam' },
  FAILED: { icon: AlertTriangle, className: 'statusFailed', label: 'Failed' },
  SENT: { icon: Send, className: 'statusDelivered', label: 'Sent' },
};

const StatCard = ({
  label,
  value,
  rate,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  rate?: number;
  icon: typeof Mail;
  color: string;
}) => (
  <div className={styles.statCard}>
    <div className={styles.statCardHeader}>
      <span className={styles.statCardLabel}>{label}</span>
      <Icon className={`${styles.statCardIcon} ${styles[color]}`} />
    </div>
    <div className={styles.statCardValue}>{value.toLocaleString()}</div>
    {rate !== undefined && (
      <div className={styles.statCardRate}>
        {rate > 0 ? (
          <ArrowUpRight className={styles.statCardRateIcon} />
        ) : (
          <span className={styles.statCardRateDash}>—</span>
        )}
        <span>{rate}%</span>
      </div>
    )}
  </div>
);

const DetailRow = ({ label, value }: { label: string; value: string | null | undefined }) => {
  if (!value) return null;
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
};

export const MailingHistory = () => {
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('all');
  const [offset, setOffset] = useState(0);
  const [allItems, setAllItems] = useState<EmailHistory[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const prevKeyRef = useRef('');
  const queryClient = useQueryClient();

  const statsPeriod =
    period === 'all' ? 'all' : period === 'today' ? 'today' : period === 'week' ? '7d' : '30d';
  const { data, isLoading, isFetching } = useEmailHistory(search, period, offset);
  const {
    data: stats,
    isLoading: statsLoading,
    isFetching: statsFetching,
  } = useEmailStats(statsPeriod);

  const refreshing = isFetching || statsFetching;

  const handleRefresh = () => {
    setOffset(0);
    queryClient.invalidateQueries({ queryKey: ['emailHistory'] });
    queryClient.invalidateQueries({ queryKey: ['emailStats'] });
  };

  useEffect(() => {
    const key = `${search}|${period}`;
    if (key !== prevKeyRef.current) {
      prevKeyRef.current = key;
      setOffset(0);
      setAllItems([]);
    }
  }, [search, period]);

  useEffect(() => {
    if (data?.items) {
      if (offset === 0) {
        setAllItems(data.items);
      } else {
        setAllItems((prev) => [...prev, ...data.items]);
      }
    }
  }, [data, offset]);

  const total = data?.total ?? 0;
  const hasMore = allItems.length < total;

  const periodOptions: [string, string][] = [
    ['all', 'All time'],
    ['today', 'Today'],
    ['week', '7 days'],
    ['month', '30 days'],
  ];

  const getStatusBadge = (item: EmailHistory) => {
    const statusKey = item.status || 'SENT';
    const config = STATUS_CONFIG[statusKey] || STATUS_CONFIG.SENT;
    const Icon = config.icon;
    return (
      <span className={`${styles.statusBadge} ${styles[config.className]}`}>
        <Icon className={styles.statusIcon} />
        {config.label}
      </span>
    );
  };

  const formatTimestamp = (ts: string | null | undefined) => {
    if (!ts) return null;
    return new Date(ts).toLocaleString();
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h1 className={styles.title}>Mailing History</h1>
            <p className={styles.subtitle}>Email campaign analytics and send history.</p>
          </div>
          <div className={styles.headerActions}>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`${styles.refreshButton} ${refreshing ? styles.refreshButtonLoading : ''}`}
            >
              <RefreshCw className={`${styles.refreshIcon} ${refreshing ? styles.spinning : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Stats overview */}
        <div className={styles.statsGrid}>
          {statsLoading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className={styles.statCard}>
                <div className={styles.statCardHeader}>
                  <div className={styles.skeletonLine} style={{ width: '60%' }} />
                </div>
                <div className={styles.skeletonLine} style={{ width: '40%', height: '1.5rem' }} />
                <div
                  className={styles.skeletonLine}
                  style={{ width: '30%', height: '0.625rem', marginTop: '0.25rem' }}
                />
              </div>
            ))
          ) : stats ? (
            <>
              <StatCard label="Total Sent" value={stats.total_sent} icon={Send} color="colorBlue" />
              <StatCard
                label="Delivered"
                value={stats.delivered}
                rate={stats.delivery_rate}
                icon={CheckCircle2}
                color="colorGreen"
              />
              <StatCard
                label="Opened"
                value={stats.opened}
                rate={stats.open_rate}
                icon={Eye}
                color="colorIndigo"
              />
              <StatCard
                label="Clicked"
                value={stats.clicked}
                rate={stats.click_rate}
                icon={MousePointerClick}
                color="colorViolet"
              />
              <StatCard
                label="Bounced"
                value={stats.bounced}
                rate={stats.bounce_rate}
                icon={XCircle}
                color="colorRed"
              />
              <StatCard label="Pending" value={stats.pending} icon={Clock} color="colorAmber" />
            </>
          ) : null}
        </div>

        {/* Filters */}
        <div className={styles.filtersCard}>
          <div className={styles.filterRow}>
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search by email, name, or business..."
                className={styles.searchInput}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className={styles.periodBtns}>
              {periodOptions.map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setPeriod(value)}
                  className={`${styles.periodBtn} ${period === value ? styles.periodBtnActive : ''}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.filterMeta}>
            <span className={styles.filterCount}>
              {total.toLocaleString()} email{total !== 1 ? 's' : ''}
            </span>
            {stats && stats.spam_reports > 0 && (
              <span className={styles.spamWarning}>
                <ShieldAlert className={styles.spamWarningIcon} />
                {stats.spam_reports} spam report{stats.spam_reports !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableCard}>
          {isLoading && allItems.length === 0 ? (
            <div className={styles.loadingState}>
              <Loader2 className={styles.spinner} />
              <span className={styles.loadingText}>Loading history...</span>
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Recipient</th>
                    <th className={styles.hiddenSm}>Business</th>
                    <th className={styles.hiddenMd}>Subject</th>
                    <th className={styles.hiddenSm}>Sent</th>
                    <th className={styles.hiddenMd}>Opens</th>
                    <th className={styles.hiddenMd}>Clicks</th>
                    <th style={{ textAlign: 'right' }}>Status</th>
                    <th style={{ width: '2rem' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {allItems.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={styles.emptyCell}>
                        {search || period !== 'all'
                          ? 'No emails match your filters.'
                          : 'No emails sent yet.'}
                      </td>
                    </tr>
                  ) : (
                    allItems.map((item) => {
                      const isExpanded = expandedId === item.id;
                      return (
                        <tr
                          key={item.id}
                          className={isExpanded ? styles.rowExpanded : ''}
                          onClick={() => setExpandedId(isExpanded ? null : item.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <div className={styles.recipientName}>
                              {item.person?.fullName || item.emailRecipient}
                            </div>
                            <div className={styles.recipientEmail}>{item.emailRecipient}</div>
                          </td>
                          <td className={styles.hiddenSm}>
                            {parseDisplayName(item.person?.place?.displayName)?.text || '—'}
                          </td>
                          <td className={`${styles.subjectCell} ${styles.hiddenMd}`}>
                            {item.subject}
                          </td>
                          <td className={`${styles.sentDate} ${styles.hiddenSm}`}>
                            {formatDate(item.sentAt)}
                          </td>
                          <td className={`${styles.engagementCell} ${styles.hiddenMd}`}>
                            {(item.openCount ?? 0) > 0 ? (
                              <span className={styles.engagementCount}>
                                <Eye className={styles.engagementIcon} />
                                {item.openCount}
                              </span>
                            ) : (
                              <span className={styles.engagementNone}>—</span>
                            )}
                          </td>
                          <td className={`${styles.engagementCell} ${styles.hiddenMd}`}>
                            {(item.clickCount ?? 0) > 0 ? (
                              <span className={styles.engagementCount}>
                                <MousePointerClick className={styles.engagementIcon} />
                                {item.clickCount}
                              </span>
                            ) : (
                              <span className={styles.engagementNone}>—</span>
                            )}
                          </td>
                          <td className={styles.statusCell}>{getStatusBadge(item)}</td>
                          <td>
                            <ChevronRight
                              className={`${styles.expandIcon} ${isExpanded ? styles.expandIconOpen : ''}`}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {/* Expanded detail panel (rendered outside table for layout) */}
              {expandedId &&
                (() => {
                  const item = allItems.find((i) => i.id === expandedId);
                  if (!item) return null;
                  return (
                    <div className={styles.detailPanel}>
                      <div className={styles.detailGrid}>
                        <div className={styles.detailSection}>
                          <h4 className={styles.detailSectionTitle}>Delivery</h4>
                          <DetailRow label="Sent at" value={formatTimestamp(item.sentAt)} />
                          <DetailRow
                            label="Delivered at"
                            value={formatTimestamp(item.deliveredAt)}
                          />
                          <DetailRow label="Status" value={item.status} />
                          {item.bounceReason && (
                            <DetailRow label="Bounce reason" value={item.bounceReason} />
                          )}
                          {item.bounceType && (
                            <DetailRow label="Bounce type" value={item.bounceType} />
                          )}
                          {item.droppedReason && (
                            <DetailRow label="Dropped reason" value={item.droppedReason} />
                          )}
                          {item.isSpamReported && <DetailRow label="Spam reported" value="Yes" />}
                        </div>
                        <div className={styles.detailSection}>
                          <h4 className={styles.detailSectionTitle}>Engagement</h4>
                          <DetailRow label="Opens" value={String(item.openCount ?? 0)} />
                          <DetailRow
                            label="First opened"
                            value={formatTimestamp(item.firstOpenedAt)}
                          />
                          <DetailRow
                            label="Last opened"
                            value={formatTimestamp(item.lastOpenedAt)}
                          />
                          <DetailRow label="Clicks" value={String(item.clickCount ?? 0)} />
                          <DetailRow
                            label="First clicked"
                            value={formatTimestamp(item.firstClickedAt)}
                          />
                          <DetailRow
                            label="Last clicked"
                            value={formatTimestamp(item.lastClickedAt)}
                          />
                          {item.clickedUrls &&
                            (() => {
                              try {
                                const urls = JSON.parse(item.clickedUrls);
                                if (urls.length > 0) {
                                  return <DetailRow label="Clicked URLs" value={urls.join(', ')} />;
                                }
                              } catch {
                                /* ignore */
                              }
                              return null;
                            })()}
                        </div>
                        <div className={styles.detailSection}>
                          <h4 className={styles.detailSectionTitle}>Content</h4>
                          <DetailRow label="Subject" value={item.subject} />
                          <DetailRow label="Template" value={item.template?.name || '—'} />
                          <DetailRow label="Recipient" value={item.emailRecipient} />
                          <DetailRow label="Contact" value={item.person?.fullName} />
                        </div>
                      </div>
                    </div>
                  );
                })()}

              {hasMore && (
                <div className={styles.loadMoreWrapper}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOffset(allItems.length);
                    }}
                    disabled={isLoading}
                    className={styles.loadMoreBtn}
                  >
                    {isLoading ? (
                      <Loader2 className={`${styles.loadMoreIcon} ${styles.spinner}`} />
                    ) : (
                      <ChevronDown className={styles.loadMoreIcon} />
                    )}
                    Load more ({total - allItems.length} remaining)
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
